import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { ChevronRightIcon, CopyIcon, CheckIcon } from "../components/ui/Icons";
import { marketplaceApi, type PublicMarketplaceSummary } from "../lib/api";

export function MarketplacesListPage() {
  const [marketplaces, setMarketplaces] = useState<PublicMarketplaceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const apiUrl =
    import.meta.env.VITE_API_URL ||
    (import.meta.env.PROD ? window.location.origin : "http://localhost:3001");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const data = await marketplaceApi.getAllPublicMarketplaces();
        setMarketplaces(data);
      } catch (err) {
        console.error("Failed to fetch marketplaces:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const copyInstallCommand = (slug: string) => {
    navigator.clipboard.writeText(
      `claude plugin marketplace add ${apiUrl}/api/marketplaces/${slug}/marketplace.json`
    );
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  return (
    <div className="min-h-screen bg-base">
      <Header />
      <div className="pt-16">
        <section className="border-b border-surface">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <h1 className="font-display text-3xl font-bold text-primary md:text-4xl">
              Marketplaces
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-muted">
              Curated collections of Claude Code plugins. Each marketplace can be added to your CLI
              with a single command.
            </p>
          </div>
        </section>

        <section className="py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {isLoading ? (
              <div className="grid gap-6 md:grid-cols-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="card-glow animate-pulse p-6">
                    <div className="mb-3 h-6 w-40 rounded bg-elevated/50" />
                    <div className="mb-2 h-4 w-full rounded bg-elevated/50" />
                    <div className="h-4 w-24 rounded bg-elevated/50" />
                  </div>
                ))}
              </div>
            ) : marketplaces.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-lg text-secondary">No marketplaces yet</p>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {marketplaces.map((m) => (
                  <div key={m.id} className="card-glow p-6">
                    <div className="flex items-start justify-between gap-4">
                      <Link to={`/m/${m.slug}`} className="group min-w-0 flex-1">
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber/20 text-amber font-display text-xl font-bold">
                            {m.name.charAt(0)}
                          </div>
                          <div>
                            <h2 className="font-display text-lg font-bold text-primary transition-colors group-hover:text-amber">
                              {m.name}
                            </h2>
                            <p className="font-mono text-xs text-muted">by {m.owner.name}</p>
                          </div>
                        </div>
                        <p className="mt-3 line-clamp-2 text-sm text-muted">
                          {m.description || "A curated plugin collection"}
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-amber">
                            {m.pluginCount} plugin{m.pluginCount !== 1 ? "s" : ""}
                          </span>
                          <ChevronRightIcon
                            size={14}
                            className="text-muted opacity-0 transition-opacity group-hover:opacity-100"
                          />
                        </div>
                      </Link>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded-lg bg-base/80 px-3 py-2 font-mono text-xs text-muted ring-1 ring-elevated">
                        claude plugin marketplace add {apiUrl}/api/marketplaces/{m.slug}
                        /marketplace.json
                      </code>
                      <button
                        onClick={() => copyInstallCommand(m.slug)}
                        className="flex shrink-0 items-center gap-1 rounded-md bg-elevated px-2 py-1.5 text-xs text-muted transition-colors hover:text-secondary"
                      >
                        {copiedSlug === m.slug ? (
                          <>
                            <CheckIcon size={12} />
                            Copied
                          </>
                        ) : (
                          <>
                            <CopyIcon size={12} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
